// ── App Entry Point ──────────────────────────────────────────────
// Single module entry referenced by <script type="module"> in listing.html.

import { initNavigation, initAssetsNavigation } from './navigation.js';
import { syncNavigationBounds } from './navigation-bounds.js';
import { initStep1 } from './step1-batch.js';
import { initStep2 } from './step2-keywords.js';
import { initStep3 } from './presentation/steps/step3/index.js';
import { resetStep3AutoSaveScope } from './step3/autosave.js';
import { initStep4 } from './step4-compliance.js';
import { initStep5 } from './step5-scoring.js';
import { initStep6 } from './step6-revision.js';
import { initDiagnosticCenter } from './modules/listing/diagnostic-center/index.js';
import { initKeywordShowcase } from './modules/listing/step4/keyword-showcase.js';
import { initStep7 } from './step7-image.js';
import { createStep7PanelFragment } from './step7/step7-template.js';
import { initAssetsWorkshop, isAssetsMode, syncWorkshopModeToggle } from './assets-workshop.js';
import { initSidebarSnapshots } from './sidebar-snapshots.js';
import { initSaveListing } from './save-listing.js';
import { restoreListingDraftPayload, normalizeSearchTermsDraft } from './listing-draft-payload.js';
import { normalizeTitlePayload } from './interaction/adapters/listing-draft.adapter.js';
import { getState, setState, setStateBulk, subscribe, restorePersistedState, setupPersistence, clearLegacyBatchJobPersistence } from './state.js';
import { initStateBridge } from './legacy/state-bridge.js';
import { initDiagnosticBridge } from './legacy/diagnostic-bridge.js';
import { seedNavigationStore, startNavigationBridge } from './legacy/navigation-bridge.js';
import { showToast } from './toast.js';
import {
  api,
} from './api.js';
import { renderAppVersionBadge } from './app-version-badge.js';
import { resolveBatchRestoreContext } from './interaction/builders/batch-restore-context.js';
import {
  createWorkbenchStateController,
  WORKBENCH_RETURN_KEY,
} from './app/workbench-state-controller.js';
import {
  hasRestoredComplianceResult,
  hasRestoredDiagnosticResult,
  hasRestoredQualityResult,
  resolveRestoredListingTargetStep,
} from './app/restored-listing-navigation.js';
import { initAuth, showAuthModal } from './app/auth-controller.js';
import { startFeatureInitializers } from './app/feature-initializer.js';
import { createProjectBinder, parseProjectFromUrl } from './modules/shared/project-bind.js';
import { projectIdOf, unwrapProject, unwrapProjectList } from './modules/shared/project-dto.js';
import { setActiveProjectBinder } from './modules/shared/project-binder-access.js';
import { request as httpRequest } from './interaction/transport/http-client.js';
import { parseJsonArray, parseJsonObject } from './app/restore-json.js';

const DEBUG_CLIENT = localStorage.getItem('debug_client') === '1';
function clientDebug(...args) {
  if (!DEBUG_CLIENT) return;
  console.debug(...args);
}

// ── Lang Toggle ─────────────────────────────────────────────────

const SUPPORTED_LANGS = ['en', 'zh'];

function getStoredLang() {
  return localStorage.getItem('listing-lang') || 'en';
}

function applyLang(lang) {
  if (!SUPPORTED_LANGS.includes(lang)) lang = 'en';
  document.body.setAttribute('data-lang', lang);
  localStorage.setItem('listing-lang', lang);

  // Update toggle button active states
  document.querySelectorAll('.lang-btn').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.lang === lang);
  });
}

function setupLangToggle() {
  // Apply stored lang on init
  applyLang(getStoredLang());

  document.querySelectorAll('.lang-toggle').forEach((toggle) => {
    toggle.querySelectorAll('.lang-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        applyLang(btn.dataset.lang || 'en');
      });
    });
  });
}

// ── Listing Restore ─────────────────────────────────────────────

function checkLegacyRestoredListing() {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('restore') === 'true') {
    const restoredData = localStorage.getItem('restoredListing');
    if (restoredData) {
      try {
        const listing = JSON.parse(restoredData);
        applyRestoredListing(listing, null);
        localStorage.removeItem('restoredListing');
        window.history.replaceState({}, '', '/listing.html');
      } catch (error) {
        console.error('Error restoring listing:', error);
        localStorage.removeItem('restoredListing');
      }
    }
  }
}

function updateBrowseOnlyBanner(show) {
  const wrap = document.getElementById('app-wrapper');
  if (!wrap) return;

  let el = document.getElementById('browse-only-banner');
  if (!show) {
    if (el) el.remove();
    return;
  }

  if (!el) {
    el = document.createElement('div');
    el.id = 'browse-only-banner';
    el.setAttribute('role', 'status');
    el.className = 'browse-only-banner';
    el.textContent =
      '只读预览：正在查看团队中他人已保存的 Listing（不会挂载其批量任务与快照）；保存已关闭，避免误写入。';
    wrap.insertBefore(el, wrap.firstChild);
  }
}

async function restoreFromApi(listingId, step) {
  try {
    const response = await fetch(`/api/saved-listings/${listingId}`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (response.status === 401) {
      sessionStorage.setItem('pendingRestore', JSON.stringify({ listingId, step }));
      showAuthModal();
      return;
    }

    if (!response.ok) throw new Error('Failed to fetch listing');

    const data = await response.json();
    const listing = data.listing;
    const browseOnly = data.browseOnly === true;
    const restoredJobId = !browseOnly && listing.jobId ? String(listing.jobId) : '';

    // Restoring a saved Listing is a product-task switch. Invalidate any old
    // autosave response and generation recovery anchor before hydrating the
    // restored draft, otherwise task A can write into restored product B.
    resetStep3AutoSaveScope();
    setStateBulk({
      batchJobId: restoredJobId,
      savedListingId: null,
      activeListingGenerationTaskId: '',
      masterRevisionInstructions: {},
    });

    updateBrowseOnlyBanner(false);
    setState('browseOnlyListing', browseOnly);

    if (restoredJobId) {
      await restoreSnapshotContext(restoredJobId);
    }

    applyRestoredListing(listing, step);
    setState('savedListingId', browseOnly ? null : parseInt(listingId, 10));

    window.history.replaceState({}, '', '/listing.html');
    sessionStorage.removeItem('pendingRestore');

    if (browseOnly) updateBrowseOnlyBanner(true);
    const { syncSaveListingButtons } = await import('./save-listing.js');
    syncSaveListingButtons();

    clientDebug('Listing restored from API:', listingId);
  } catch (error) {
    console.error('Error restoring listing from API:', error);
  }
}

async function restoreSnapshotContext(jobId) {
  let batchJob = null;
  let savedState = {};

  try {
    batchJob = await api.getBatchJob(jobId);
  } catch (err) {
    clientDebug('[Restore] 无法读取批任务当前上下文:', err.message);
  }

  try {
    const data = await api.getSnapshots(jobId);
    const snapshots = data.snapshots || [];
    const latest = snapshots[0];
    savedState = parseJsonObject(latest?.state, {});
  } catch (err) {
    clientDebug('[Restore] 无法读取历史页面快照:', err.message);
  }

  if (!batchJob && !Object.keys(savedState).length) return;

  const context = resolveBatchRestoreContext({ batchJob, savedState });
  const marketDataKeywords = savedState.marketDataKeywords || savedState.mcpKeywords || [];
  setStateBulk({
    batchJobId: jobId,
    batchJob: context.batchJob,
    keywordLibrary: context.keywordLibrary,
    businessContext: context.businessContext,
    keywordUsePolicies: context.keywordUsePolicies,
    competitorRoles: context.competitorRoles,
    batchSnapshots: context.batchSnapshots,
    competitorAsinsIncluded: new Set(context.batchSnapshots.map((snapshot) => snapshot.asin)),
    snapshotState: context.snapshotState,
    productInfo: context.productInfo,
    ...(context.marketplace ? { marketplace: context.marketplace } : {}),
    selectedStKeywords: new Set(savedState.selectedStKeywords || []),
    marketDataKeywords,
    mcpKeywords: savedState.mcpKeywords || marketDataKeywords,
  });
}

function collectKeywordsFromSnapshot(snapshot, limit = 80) {
  const out = [];
  const add = (value) => {
    const text = String(value?.keyword || value?.term || value?.text || value || '').trim();
    if (text && !out.includes(text)) out.push(text);
  };
  const categories = snapshot?.categories || {};
  Object.values(categories).forEach((list) => {
    if (Array.isArray(list)) list.forEach(add);
  });
  if (Array.isArray(snapshot?.keywords)) snapshot.keywords.forEach(add);
  if (Array.isArray(snapshot)) snapshot.forEach(add);
  return out.slice(0, limit);
}

function buildLegacyOptimizerResultFromSavedListing(listing, draft) {
  const keywords = collectKeywordsFromSnapshot(listing?.keywordsSnapshot, 80);
  if (!keywords.length) return null;
  const text = [
    draft?.listingDraftTitle?.titleEn,
    ...(draft?.listingDraftBullets?.bulletsEn || []),
    draft?.listingDraftDescription?.descriptionEn,
  ].join(' ').toLowerCase();
  const coveredCore = keywords.filter((kw) => text.includes(String(kw).toLowerCase())).slice(0, 12);
  const missing = keywords.filter((kw) => !text.includes(String(kw).toLowerCase()));
  return {
    provider: 'restored',
    mode: 'saved_listing_restore',
    coveredCore,
    missingCore: missing.slice(0, 8),
    missingTrafficKeywords: missing.slice(8, 16),
    lostWords: [],
    suggestions: [],
  };
}

function applyRestoredListing(listing, stepOverride) {
  setState('selectedTitleVersion', 0);
  setState('exportText', '');
  setState('exportTextZh', '');
  setState('exportShowingZh', false);
  setState('searchTermsTranslationText', '');
  setState('searchTermsTranslationLoading', false);
  setState('searchTermsTranslationError', '');
  setState('searchTermsTranslationWarning', '');
  setState('searchTermsShowingZh', false);

  const titleVersion = parseJsonArray(listing.titleVersion, []);
  const metadata = listing.metadata && typeof listing.metadata === 'object' ? listing.metadata : {};
  const restoredDraft = metadata.listingDraftTitle || metadata.listingDraftBullets || metadata.listingDraftDescription
    ? {
        listingDraftTitle: metadata.listingDraftTitle ? normalizeTitlePayload(metadata.listingDraftTitle) : null,
        listingDraftBullets: metadata.listingDraftBullets || null,
        listingDraftDescription: metadata.listingDraftDescription || null,
        listingDraftSearchTerms: metadata.listingDraftSearchTerms || null,
        listingDraftQa: metadata.listingDraftQa || null,
        listingDraftV1: metadata.listingDraftV1 || null,
        optimizerResult: metadata.optimizerResult || null,
        listingGenerationMeta: metadata.listingGenerationMeta || null,
      }
    : {
        listingDraftTitle: listing.title ? normalizeTitlePayload({
          titleEn: listing.title,
          titleZh: '',
          titleOptions: Array.isArray(titleVersion) ? titleVersion : [],
          isSmartGenerated: true,
        }) : null,
        listingDraftBullets: listing.bullets ? {
          bulletsEn: parseJsonArray(listing.bullets, []),
          bulletsZh: [],
          isSmartGenerated: true,
        } : null,
        listingDraftDescription: listing.description ? {
          descriptionEn: listing.description,
          descriptionZh: '',
          isSmartGenerated: true,
        } : null,
        listingDraftSearchTerms: listing.searchTermsDraft
          ? normalizeSearchTermsDraft(listing.searchTermsDraft)
          : (listing.searchTerms ? normalizeSearchTermsDraft(listing.searchTerms) : null),
        listingDraftQa: listing.qa ? (typeof listing.qa === 'string' ? parseJsonArray(listing.qa, []) : listing.qa) : null,
      };
  if (!restoredDraft.optimizerResult) {
    restoredDraft.optimizerResult = buildLegacyOptimizerResultFromSavedListing(listing, restoredDraft);
  }

  restoreListingDraftPayload({
    ...restoredDraft,
    listingGenerationMeta: restoredDraft.listingGenerationMeta || metadata.listingGenerationMeta || null,
    keywordLibrary: listing.keywordsSnapshot || null,
  });
  const marketplace = listing.marketplace || metadata?.businessContext?.marketplace || null;
  const asin = listing.asin
    || metadata?.listingGenerationMeta?.mergeMeta?.asins?.[0]
    || getState('productInfo')?.asin
    || null;
  if (marketplace) {
    setState('marketplace', marketplace);
  }
  if (marketplace || asin) {
    setState('productInfo', {
      ...(getState('productInfo') || {}),
      ...(marketplace ? { marketplace } : {}),
      ...(asin ? { asin: String(asin).trim().toUpperCase() } : {}),
    });
  }
  setState(
    'businessContext',
    metadata.businessContext && typeof metadata.businessContext === 'object'
      ? metadata.businessContext
      : null,
  );
  setState(
    'keywordUsePolicies',
    Array.isArray(metadata.keywordUsePolicies) ? metadata.keywordUsePolicies : [],
  );
  setState(
    'competitorRoles',
    Array.isArray(metadata.competitorRoles) ? metadata.competitorRoles : [],
  );

  // `complianceViolations: []` is the saved-row default, not a completed
  // diagnostic. Only restore Step 04 state when there is real evidence.
  const hasRestoredCompliance = hasRestoredComplianceResult(listing);
  const hasRestoredDiagnostic = hasRestoredDiagnosticResult(listing);
  const hasRestoredQuality = hasRestoredQualityResult(listing);
  if (hasRestoredCompliance) {
    setState('complianceResult', {
      score: listing.complianceScore || 0,
      violations: listing.complianceViolations || []
    });
  }

  if (hasRestoredQuality) {
    setState('scoringReport', {
      totalScore: listing.qualityScore || 0,
      dimensions: listing.qualityDimensions || {}
    });
  }

  const hasContent = !!(listing.title || listing.bullets || listing.description || listing.searchTerms);
  const hasKw = !!(getState('keywordLibrary')?.keywords?.length || getState('batchJobId'));
  setState('stepReady', {
    1: true,
    2: hasKw || hasContent,
    3: hasContent,
    // 无 batch 的已保存 Listing 仍可直接打开步骤 3
    4: hasRestoredDiagnostic,
  });

  syncNavigationBounds();
  const urlStep = stepOverride != null && stepOverride !== ''
    ? parseInt(stepOverride, 10)
    : parseInt(new URLSearchParams(window.location.search).get('step'), 10);
  setState('currentStep', resolveRestoredListingTargetStep({
    hasContent,
    requestedStep: urlStep,
    maxNavigableStep: getState('highestStep'),
  }));
}

// ── App Config Fetch ─────────────────────────────────────────────

async function loadAppConfig() {
  try {
    const res = await fetch('/api/config');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const config = await res.json();
    const statusEl = document.getElementById('system-status');
    if (config?.hasApiKey) {
      statusEl.textContent = '就绪';
      statusEl.className = 'status-ready';
    } else {
      statusEl.textContent = '缺少 API Key';
      statusEl.className = 'status-warn';
    }
  } catch {
    const statusEl = document.getElementById('system-status');
    statusEl.textContent = '连接失败';
    statusEl.className = 'status-warn';
  }
}

// ── Step Readiness Manager ───────────────────────────────────────
// Tracks when each step's data is ready so users can advance.
// stepReady gates the four sidebar steps; optimization submodules live inside Step 4.

export function markStepReady(step, ready = true) {
  const current = getState('stepReady') || {};
  if (current[step] === ready) return;
  setState('stepReady', { ...current, [step]: ready });
  syncNavigationBounds();
}

function initStepReadinessManager() {
  function hasCompleteStep3Draft() {
    const meta = getState('listingGenerationMeta');
    if (meta?.readiness && meta.readiness.canProceedToFullTextReview === false) {
      return false;
    }
    const title = getState('listingDraftTitle');
    const bullets = getState('listingDraftBullets');
    const titleOk = title && !title.error && (title.titleEn || title.titleOptions?.length);
    const bulletsOk = bullets && !bullets.error && bullets.bulletsEn?.length >= 5;
    return !!(titleOk && bulletsOk);
  }

  function updateStep3Ready() {
    markStepReady(3, hasCompleteStep3Draft());
  }

  // Step 2: keyword library is ready when batch job completes and library exists
  subscribe('state:keywordLibraryChanged', ({ curr }) => {
    if (curr && curr.keywords?.length > 0) {
      markStepReady(2);
    }
  });

  // Step 3: ready only after a title and a full 5-bullet draft exist.
  subscribe('state:listingDraftTitleChanged', updateStep3Ready);
  subscribe('state:listingDraftBulletsChanged', updateStep3Ready);
  subscribe('state:listingGenerationMetaChanged', updateStep3Ready);

  // Step 4: compliance result ready after check runs
  subscribe('state:complianceResultChanged', ({ curr }) => {
    if (curr && (curr.violations !== undefined || curr.score !== undefined)) {
      markStepReady(4);
    }
  });

  subscribe('state:scoringReportChanged', ({ curr }) => {
    if (curr && curr.totalScore !== undefined) {
      markStepReady(4);
    }
  });

  subscribe('state:revisionSuggestionsChanged', ({ curr }) => {
    if (Array.isArray(curr) && curr.length > 0) {
      markStepReady(4);
    }
  });
}

// ── Workbench State Persistence ────────────────────────────────
// Save/restore work-in-progress state when navigating to system management.

const workbenchStateController = createWorkbenchStateController({
  getState,
  setState,
  setStateBulk,
  syncNavigationBounds,
});
const {
  clearSessionArtifacts: clearWorkbenchSessionArtifacts,
  clearListingTaskPersistence,
  save: saveWorkbenchState,
  restore: restoreWorkbenchState,
  initAssetsMode: initAssetsModeBootstrap,
} = workbenchStateController;

function startListingFeatureInitializers(features) {
  startFeatureInitializers(features, {
    onFailure: (name) => {
      showToast(`${name}未能初始化，其他功能仍可继续使用。请刷新页面后重试。`, {
        id: 'listing-bootstrap-init-error',
        type: 'error',
        duration: 6200,
      });
    },
  });
}

document.addEventListener('DOMContentLoaded', async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const restoreId = urlParams.get('restoreId');
  const legacyRestore = urlParams.get('restore') === 'true';
  const assetsMode = isAssetsMode();

  // 1. Auth
  const shouldInitializeWorkbench = await initAuth({
    onLogout: clearWorkbenchSessionArtifacts,
  });
  renderAppVersionBadge();

  if (!shouldInitializeWorkbench) {
    return;
  }

  window.addEventListener('auth:unauthorized', () => {
    showAuthModal();
  });

  syncWorkshopModeToggle(assetsMode);

  // 项目绑定组件初始化（策略 A：必须有项目才能保存）
  initProjectBindBar();

  if (assetsMode) {
    startListingFeatureInitializers([
      { name: '素材工作台状态', initialize: () => { initAssetsModeBootstrap(); } },
      { name: '工作台状态保存', initialize: () => { setupPersistence(); } },
      { name: '工作台状态桥接', initialize: () => { initStateBridge({ getState, setState, subscribe }); } },
      { name: '诊断状态桥接', initialize: () => { initDiagnosticBridge({ getState, setState, subscribe }); } },
      { name: '图片工作台面板', initialize: () => { mountListingStep7Template(); } },
      { name: '素材工作台', initialize: () => { initAssetsWorkshop(); } },
      { name: '素材工作台导航', initialize: () => { initAssetsNavigation(); } },
      { name: '图片生成', initialize: () => { initStep7(); } },
      { name: '语言切换', initialize: () => { setupLangToggle(); } },
      { name: '系统状态', initialize: () => { loadAppConfig(); } },
      { name: '系统管理入口', initialize: () => { bindSystemAdminBtn(); } },
    ]);
    return;
  }

  const shouldRestoreWorkbench = sessionStorage.getItem(WORKBENCH_RETURN_KEY) === '1';

  // 只有显式恢复或从系统管理主动返回时才继承工作台上下文。
  // 浏览器刷新始终从新 Listing 任务开始，避免被 sessionStorage 或旧 URL 被动带回 Step 03。
  if (restoreId || legacyRestore) {
    restorePersistedState(['batchJobId', 'savedListingId', 'activeListingGenerationTaskId']);
  } else if (!shouldRestoreWorkbench) {
    clearListingTaskPersistence();
  }
  setupPersistence();

  // 1.0a State bridge: sync listing-draft store ↔ state.js (bidirectional)
  initStateBridge({ getState, setState, subscribe });
  // 1.0b Diagnostic bridge: sync diagnostic store ↔ state.js (bidirectional)
  initDiagnosticBridge({ getState, setState, subscribe });

  // 1.1 数据恢复（须在 initNavigation 之前完成，避免侧栏/面板闪错）
  if (restoreId) {
    await restoreFromApi(restoreId, urlParams.get('step'));
  } else if (legacyRestore) {
    checkLegacyRestoredListing();
  } else if (shouldRestoreWorkbench) {
    sessionStorage.removeItem(WORKBENCH_RETURN_KEY);
    if (restoreWorkbenchState()) {
      // workbench 已在 restoreWorkbenchState 内 syncNavigationBounds
    } else {
      clearWorkbenchSessionArtifacts();
      setState('currentStep', 1);
      syncNavigationBounds();
      showToast('未能恢复上次工作台状态，请重新进入流程。', {
        id: 'workbench-state-toast',
        type: 'error',
        duration: 4200,
      });
    }
  } else {
    clearWorkbenchSessionArtifacts();
    clearLegacyBatchJobPersistence();
    clearListingTaskPersistence();
    const urlStep = parseInt(urlParams.get('step'), 10);
    if (!Number.isFinite(urlStep) || urlStep < 1 || urlStep > 4) {
      setState('currentStep', 1);
    }
    // Reset highestStep on fresh load so sidebar doesn't show stale unlocked steps
    setState('highestStep', 1);
    setState('batchJobId', '');
    setState('savedListingId', null);
    syncNavigationBounds();
  }

  // Navigation store bridge: seed + start bidirectional sync
  seedNavigationStore();
  startNavigationBridge();

  startListingFeatureInitializers([
    { name: '流程导航', initialize: () => { initNavigation(); } },
    // Step readiness subscriptions must exist before batch resume / Step1.
    { name: '流程就绪状态', initialize: () => { initStepReadinessManager(); } },
    { name: '商品批量分析', initialize: () => { initStep1(); } },
    // Restored Listings hydrate their draft before the feature modules initialize.
    // Bind Step3 before Step2 so its first render and tab controls stay available.
    { name: 'Listing 生成', initialize: () => { initStep3(); } },
    { name: '关键词库', initialize: () => { initStep2(); } },
    { name: '合规诊断', initialize: () => { initStep4(); } },
    { name: '质量评分', initialize: () => { initStep5(); } },
    { name: '改稿建议', initialize: () => { initStep6(); } },
    { name: '诊断中心', initialize: () => { initDiagnosticCenter(); } },
    { name: '关键词展示', initialize: () => { initKeywordShowcase(); } },
    // Image suite is mounted for compatibility; primary entry is /image-suite.html.
    { name: '图片工作台面板', initialize: () => { mountListingStep7Template(); } },
    { name: '图片生成', initialize: () => { initStep7(); } },
    { name: '快照侧栏', initialize: () => { initSidebarSnapshots(); } },
    { name: 'Listing 保存', initialize: () => { initSaveListing(); } },
    { name: '语言切换', initialize: () => { setupLangToggle(); } },
    { name: '系统状态', initialize: () => { loadAppConfig(); } },
    { name: '系统管理入口', initialize: () => { bindSystemAdminBtn(); } },
  ]);
});

function bindSystemAdminBtn() {
  const systemAdminBtn = document.getElementById('system-admin-btn');
  if (systemAdminBtn) {
    systemAdminBtn.addEventListener('click', () => {
      if (isAssetsMode()) {
        window.location.href = '/system-management.html';
        return;
      }
      const saved = saveWorkbenchState();
      showToast(
        saved.ok ? '工作台状态已保存，正在进入系统管理。' : '工作台状态保存失败，进入后可能无法恢复。',
        {
          id: 'workbench-state-toast',
          type: saved.ok ? 'success' : 'error',
          duration: saved.ok ? 1200 : 2600,
        }
      );
      window.setTimeout(() => {
        window.location.href = '/system-management.html';
      }, saved.ok ? 350 : 900);
    });
  }
}

function mountListingStep7Template() {
  const slot = document.getElementById('step-image-slot');
  if (!slot) return;
  slot.replaceChildren(createStep7PanelFragment({ context: 'listing' }));
}

// ── Project Bind Bar ─────────────────────────────────────────────
// 策略 A：Listing 保存必须有新品项目。组件挂载在顶部，切换项目时写入 state.newProductProjectId。

let projectBinder = null;

function initProjectBindBar() {
  const container = document.getElementById('project-bind-bar');
  if (!container) return;
  const initialProjectId = parseProjectFromUrl();

  async function fetchProjects({ page = 1, size = 50, search = '' } = {}) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('pageSize', String(size));
    if (search) params.set('search', String(search));
    const qs = params.toString();
    const result = await httpRequest(`/api/new-product-projects${qs ? `?${qs}` : ''}`);
    return unwrapProjectList(result);
  }

  async function fetchProject(id) {
    const result = await httpRequest(`/api/new-product-projects/${id}`);
    return unwrapProject(result);
  }

  projectBinder = createProjectBinder({
    container,
    initialProjectId,
    // URL fromProject is upstream inheritance — hide change until unlocked.
    lockOnInitial: Boolean(initialProjectId),
    fetchProjects,
    fetchProject,
    onChange: (project) => {
      const pid = projectIdOf(project);
      setState('newProductProjectId', pid);
    },
  });
  setActiveProjectBinder(projectBinder);
}

/** Shared accessor so generation steps can lock the binder after a task is created. */
export function getProjectBinder() {
  return projectBinder;
}
